import { useState, useEffect, useCallback, type RefObject } from 'react'
import type { CanvasPosition } from '../../../../types/infrastructure-map'

interface UseDotDragArgs {
  nodeRef: RefObject<HTMLDivElement | null>
  scale: number
  position: CanvasPosition
  onDrag?: (position: CanvasPosition) => void
  onDragEnd?: (position: CanvasPosition) => void
}

interface UseDotDragResult {
  isDragging: boolean
  handleMouseDown: (e: React.MouseEvent) => void
}

export function useDotDrag({
  nodeRef,
  scale,
  position,
  onDrag,
  onDragEnd,
}: UseDotDragArgs): UseDotDragResult {
  const [isDragging, setIsDragging] = useState(false)
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })

  const handleMouseDown = (e: React.MouseEvent): void => {
    if (e.button !== 0 || !onDrag) return
    e.preventDefault()
    e.stopPropagation()
    const rect = nodeRef.current?.getBoundingClientRect()
    if (!rect) return
    setDragOffset({
      x: (e.clientX - rect.left - rect.width / 2) / scale,
      y: (e.clientY - rect.top - rect.height / 2) / scale,
    })
    setIsDragging(true)
  }

  const handleMouseMove = useCallback(
    (e: MouseEvent): void => {
      if (!isDragging || !nodeRef.current?.parentElement || !onDrag) return
      const parentRect = nodeRef.current.parentElement.getBoundingClientRect()
      onDrag({
        x: (e.clientX - parentRect.left) / scale - dragOffset.x,
        y: (e.clientY - parentRect.top) / scale - dragOffset.y,
      })
    },
    [isDragging, dragOffset, scale, onDrag, nodeRef]
  )

  const handleMouseUp = useCallback((): void => {
    if (isDragging && onDragEnd) {
      setIsDragging(false)
      onDragEnd(position)
    }
  }, [isDragging, position, onDragEnd])

  useEffect(() => {
    if (!isDragging) return
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  return { isDragging, handleMouseDown }
}
